import React, {
  DetailedHTMLProps,
  forwardRef,
  UIEvent,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import {
  pluralize,
  isEmptyString,
} from '@populateui/utils';
import Button from 'pages/Dashboard/components/Button';
import Spinner from 'pages/Dashboard/components/Spinner';
import head from 'lodash/head';
import AddIcon from '@mui/icons-material/Add';
import clsx from 'clsx';

type NewOptionProps = {
  inputValue: string;
  onAddNew: () => void;
  type: string;
  label: string;
};

function NewOption({
  inputValue,
  onAddNew,
  type,
  label = 'New',
}: NewOptionProps) {
  const preText = [label, type].join(' ')?.trim();

  return (
    <div
      tabIndex={-1}
      role="option"
      onClick={() => { onAddNew(); }}
      aria-selected="false"
      data-option-index={0}
      onKeyDown={noop}
      className="MuiAutocomplete-option flex items-center w-full gap-1 text-xs p-2 hover:bg-gray-100 rounded"
    >
      <AddIcon className="!w-4 !h-4 !text-inherit" />
      {preText}
      <strong>{inputValue}</strong>
    </div>
  );
}

// should only be used by the Lookup component: /src/views/EMR/components/Lookup/index.tsx
const Dropdown = forwardRef(({
  children,
  className,
  objectName = 'Result',
  count,
  pageProperties,
  isLoadingMore,
  fetchNextPage = noop,
  showTotalSize = false,
  searchValue,
  isSearching = false,
  awaitingInitialSearch = false,
  emptyText = 'No options found',
  onAddNew,
  retainScrollOnSelection = false,
  selectionCount = 0,
  label,
  type = '',
  ...props
}: DetailedHTMLProps<any, any>, ref) => {
  const listboxElementRef = useRef<HTMLDivElement | null>(null);
  const scrollTopRef = useRef<number>(0);
  const previousSelectionCountRef = useRef<number>(selectionCount);

  useEffect(() => {
    const selectionChanged = previousSelectionCountRef.current !== selectionCount;
    const listboxElement = listboxElementRef.current;
    let animationFrame: number | undefined;
    previousSelectionCountRef.current = selectionCount;

    if (retainScrollOnSelection && selectionChanged && !isNil(listboxElement)) {
      const scrollTop = scrollTopRef.current;
      animationFrame = window.requestAnimationFrame(() => {
        listboxElement.scrollTop = scrollTop;
      });
    }

    return () => {
      if (!isNil(animationFrame)) {
        window.cancelAnimationFrame(animationFrame);
      }
    };
  }, [retainScrollOnSelection, selectionCount]);

  const handleListboxScroll = (event: UIEvent<HTMLDivElement>) => {
    listboxElementRef.current = event.currentTarget;
    scrollTopRef.current = event.currentTarget.scrollTop;
    props.onScroll?.(event);
  };

  // always remove the first option,
  // as for placeholder customization control there will always be an empty option
  const shouldRemoveFirstOption = true;

  const noOptionsText = useMemo(() => {
    const originalCount = count - Number(shouldRemoveFirstOption);
    const messages = [];
    if (isSearching) {
      messages.push('Searching..');
    } else if (isEmptyString(searchValue) && originalCount === 0 && awaitingInitialSearch) {
      messages.push('Start typing to see the list');
    } else if (originalCount === 0) {
      messages.push(emptyText);
    }

    const message = head(messages);
    const helperText = isNil(message) ? undefined : (
      <div className="text-xs text-gray-400">
        {message}
      </div>
    );
    return originalCount === 0 ? helperText : undefined;
  }, [isSearching, searchValue, count, shouldRemoveFirstOption, awaitingInitialSearch, emptyText]);

  const placeholder = useMemo(() => (
    !isNil(onAddNew)
      ? (
        <NewOption
          inputValue={searchValue}
          onAddNew={onAddNew}
          label={label}
          type={type}
        />
      )
      : noOptionsText
  ), [noOptionsText, onAddNew, searchValue]);

  return (
    <div className="h-full relative -m-2">
      <div
        {...props}
        className={clsx(className, '!px-2 !pb-2')}
        ref={ref}
        role="listbox"
        onScroll={handleListboxScroll}
      >
        {placeholder}
        {(children ?? []).slice(Number(shouldRemoveFirstOption))}
        {pageProperties?.hasNextPage && (
        <Button
          className="!hover:bg-transparent !font-medium !text-gray-400 !w-full !text-center !p-2 !max-w-none !flex items-center !flex-row"
          variant="text"
          onClick={fetchNextPage}
          disabled={isLoadingMore}
        >
          {isLoadingMore && <Spinner className="!mr-2" />}
          <div>{isLoadingMore ? 'Loading...' : 'Load More'}</div>
        </Button>
        )}
      </div>
      {!isNil(pageProperties) && showTotalSize && (
      <div className="p-2 text-xs sticky bottom-0">
        {pluralize(objectName, pageProperties?.totalSize)}
        {' '}
        Found
      </div>
      )}
    </div>
  );
});

export default Dropdown;
